import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

export default function Sales() {
  const { isAdmin } = useAuth();
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({});
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editSale, setEditSale] = useState(null);
  const [filters, setFilters] = useState({
    category: "",
    model: "",
    color: "",
  });
  const [form, setForm] = useState({
    productId: "",
    color: "",
    quantity: 1,
    sellPrice: 0,
    note: "",
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeTab, setActiveTab] = useState("list");

  const fetchSales = async () => {
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    ).toString();
    const res = await axios.get(
      `https://vape-backend-1.onrender.com/api/sales?${params}`,
    );
    setSales(res.data.sales);
    setSummary(res.data.summary);
  };
  const fetchProducts = async () => {
    const res = await axios.get(
      "https://vape-backend-1.onrender.com/api/products",
    );
    setProducts(res.data);
  };

  useEffect(() => {
    fetchSales();
  }, [filters]);
  useEffect(() => {
    fetchProducts();
  }, []);

  const handleProductChange = (id) => {
    const p = products.find((x) => x._id === id);
    setSelectedProduct(p);
    setForm({ ...form, productId: id, color: "", sellPrice: 0 });
  };
  const handleColorChange = (color) => {
    const c = selectedProduct?.colors.find((c) => c.color === color);
    setForm({ ...form, color, sellPrice: c?.sellPrice || 0 });
  };

  const openAdd = () => {
    setEditSale(null);
    setForm({ productId: "", color: "", quantity: 1, sellPrice: 0, note: "" });
    setSelectedProduct(null);
    setShowModal(true);
  };
  const openEdit = (sale) => {
    setEditSale(sale);
    setForm({
      productId: sale.product,
      color: sale.color,
      quantity: sale.quantity,
      sellPrice: sale.sellPrice,
      note: sale.note || "",
    });
    setSelectedProduct(products.find((x) => x._id === sale.product));
    setShowModal(true);
  };

  const submit = async () => {
    if (!editSale && (!form.productId || !form.color || !form.quantity))
      return toast.error("Fill all fields");
    try {
      if (editSale) {
        await axios.put(
          `https://vape-backend-1.onrender.com/api/sales/${editSale._id}`,
          {
            quantity: form.quantity,
            sellPrice: form.sellPrice,
            note: form.note,
          },
        );
        toast.success("Sale updated!");
      } else {
        await axios.post("https://vape-backend-1.onrender.com/api/sales", form);
        toast.success(isAdmin ? "Sale recorded!" : "Submitted for approval!");
      }
      setShowModal(false);
      fetchSales();
      fetchProducts();
      setForm({
        productId: "",
        color: "",
        quantity: 1,
        sellPrice: 0,
        note: "",
      });
      setSelectedProduct(null);
      setEditSale(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const deleteSale = async (id) => {
    if (!window.confirm("Delete this sale? Stock will be restored.")) return;
    try {
      await axios.delete(`https://vape-backend-1.onrender.com/api/sales/${id}`);
      toast.success("Sale deleted, stock restored");
      fetchSales();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const allColors = [...new Set(sales.map((s) => s.color))];

  // ---- breakdown ----
  const buildBreakdown = () => {
    const map = {};
    sales.forEach((s) => {
      if (!map[s.category]) map[s.category] = {};
      if (!map[s.category][s.model]) map[s.category][s.model] = {};
      if (!map[s.category][s.model][s.color])
        map[s.category][s.model][s.color] = {
          qty: 0,
          revenue: 0,
          cost: 0,
          profit: 0,
        };
      map[s.category][s.model][s.color].qty += s.quantity;
      map[s.category][s.model][s.color].revenue += s.totalSellRevenue;
      map[s.category][s.model][s.color].cost += s.totalBuyCost;
      map[s.category][s.model][s.color].profit += s.profit;
    });
    return map;
  };
  const breakdown = buildBreakdown();

  const getColorStock = (category, model, color) => {
    const p = products.find(
      (x) =>
        x.category.toLowerCase() === category.toLowerCase() &&
        x.model.toLowerCase() === model.toLowerCase(),
    );
    if (!p) return "-";
    const c = p.colors.find((x) => x.color === color);
    return c ? c.stock : 0;
  };

  const tabStyle = (t) => ({
    padding: "8px 18px",
    borderRadius: 8,
    border: "1px solid",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    background: activeTab === t ? "var(--accent)" : "transparent",
    color: activeTab === t ? "#0a0d14" : "var(--text-secondary)",
    borderColor: activeTab === t ? "var(--accent)" : "var(--border)",
    transition: "all 0.2s",
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sales</h1>
          <p className="page-subtitle">Track all product sales and profits</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          + Record Sale
        </button>
      </div>

      {/* Summary cards */}
      <div className="stat-grid">
        <div className="stat-card cyan">
          <div className="stat-label">Total Revenue</div>
          <div className="stat-value">
            ৳{(summary.totalRevenue || 0).toLocaleString()}
          </div>
          <div className="stat-sub">Buy cost + profit একসাথে</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Total Profit</div>
          <div className="stat-value">
            ৳{(summary.totalProfit || 0).toLocaleString()}
          </div>
          <div className="stat-sub">Revenue − buy cost</div>
        </div>
        <div className="stat-card yellow">
          <div className="stat-label">Total Buy Cost</div>
          <div className="stat-value">
            ৳{(summary.totalCost || 0).toLocaleString()}
          </div>
          <div className="stat-sub">কত টাকায় কেনা হয়েছে</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Units Sold</div>
          <div className="stat-value">{summary.totalQty || 0}</div>
          <div className="stat-sub">মোট বিক্রি হয়েছে</div>
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}
      >
        <button style={tabStyle("list")} onClick={() => setActiveTab("list")}>
          📋 Sales List
        </button>
        <button
          style={tabStyle("breakdown")}
          onClick={() => setActiveTab("breakdown")}
        >
          📊 Color-wise Breakdown
        </button>
      </div>

      {/* ── SALES LIST TAB ── */}
      {activeTab === "list" && (
        <>
          <div className="filter-bar">
            <input
              placeholder="Category..."
              value={filters.category}
              onChange={(e) =>
                setFilters({ ...filters, category: e.target.value })
              }
            />
            <input
              placeholder="Model..."
              value={filters.model}
              onChange={(e) =>
                setFilters({ ...filters, model: e.target.value })
              }
            />
            <select
              value={filters.color}
              onChange={(e) =>
                setFilters({ ...filters, color: e.target.value })
              }
            >
              <option value="">All Colors</option>
              {allColors.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Desktop table */}
          <div className="card hide-mobile">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Model</th>
                    <th>Color</th>
                    <th>Qty</th>
                    <th>Buy ৳</th>
                    <th>Sell ৳</th>
                    <th>Buy Cost</th>
                    <th>Revenue</th>
                    <th>Profit</th>
                    <th>Date</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sales.length === 0 && (
                    <tr>
                      <td colSpan={isAdmin ? 12 : 11}>
                        <div className="empty-state">
                          <div className="icon">💰</div>
                          <p>No sales recorded</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {sales.map((s) => (
                    <tr key={s._id}>
                      <td style={{ fontWeight: 600 }}>{s.productName}</td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        {s.category}
                      </td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        {s.model}
                      </td>
                      <td>
                        <span
                          className="badge-color"
                          style={{ background: "var(--bg-hover)" }}
                        >
                          {s.color}
                        </span>
                      </td>
                      <td>{s.quantity}</td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        ৳{s.buyPrice}
                      </td>
                      <td>৳{s.sellPrice}</td>
                      <td className="text-loss">
                        ৳{s.totalBuyCost?.toLocaleString()}
                      </td>
                      <td className="text-accent">
                        ৳{s.totalSellRevenue?.toLocaleString()}
                      </td>
                      <td
                        className={s.profit >= 0 ? "text-profit" : "text-loss"}
                        style={{ fontWeight: 700 }}
                      >
                        ৳{s.profit?.toLocaleString()}
                      </td>
                      <td
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "0.78rem",
                        }}
                      >
                        {new Date(s.soldAt).toLocaleDateString("en-BD")}
                      </td>
                      {isAdmin && (
                        <td style={{ whiteSpace: "nowrap" }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => openEdit(s)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            style={{ marginLeft: 4 }}
                            onClick={() => deleteSale(s._id)}
                          >
                            Del
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="show-mobile">
            {sales.length === 0 && (
              <div className="card">
                <div className="empty-state">
                  <div className="icon">💰</div>
                  <p>No sales recorded</p>
                </div>
              </div>
            )}
            {sales.map((s) => (
              <div
                className="card"
                key={s._id}
                style={{ marginBottom: 10, padding: 14 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                      {s.productName}
                    </div>
                    <div
                      style={{
                        fontSize: "0.78rem",
                        color: "var(--text-secondary)",
                        marginTop: 2,
                      }}
                    >
                      {s.category} · {s.model}
                    </div>
                  </div>
                  <span
                    className="badge-color"
                    style={{ background: "var(--bg-hover)", flexShrink: 0 }}
                  >
                    {s.color}
                  </span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 6,
                    fontSize: "0.8rem",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      background: "var(--bg-primary)",
                      borderRadius: 6,
                      padding: "6px 10px",
                    }}
                  >
                    <div
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.68rem",
                        marginBottom: 2,
                      }}
                    >
                      QTY
                    </div>
                    <strong>{s.quantity}</strong>
                  </div>
                  <div
                    style={{
                      background: "var(--bg-primary)",
                      borderRadius: 6,
                      padding: "6px 10px",
                    }}
                  >
                    <div
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.68rem",
                        marginBottom: 2,
                      }}
                    >
                      SELL PRICE
                    </div>
                    <strong>৳{s.sellPrice}</strong>
                  </div>
                  <div
                    style={{
                      background: "var(--bg-primary)",
                      borderRadius: 6,
                      padding: "6px 10px",
                    }}
                  >
                    <div
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.68rem",
                        marginBottom: 2,
                      }}
                    >
                      REVENUE
                    </div>
                    <strong className="text-accent">
                      ৳{s.totalSellRevenue?.toLocaleString()}
                    </strong>
                  </div>
                  <div
                    style={{
                      background: "var(--bg-primary)",
                      borderRadius: 6,
                      padding: "6px 10px",
                    }}
                  >
                    <div
                      style={{
                        color: "var(--text-muted)",
                        fontSize: "0.68rem",
                        marginBottom: 2,
                      }}
                    >
                      PROFIT
                    </div>
                    <strong
                      className={s.profit >= 0 ? "text-profit" : "text-loss"}
                    >
                      ৳{s.profit?.toLocaleString()}
                    </strong>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}
                  >
                    {new Date(s.soldAt).toLocaleDateString("en-BD")}
                  </span>
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => openEdit(s)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteSale(s._id)}
                      >
                        Del
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── BREAKDOWN TAB ── */}
      {activeTab === "breakdown" && (
        <div>
          {Object.keys(breakdown).length === 0 && (
            <div className="card">
              <div className="empty-state">
                <div className="icon">📊</div>
                <p>No sales data yet</p>
              </div>
            </div>
          )}
          {Object.entries(breakdown).map(([category, models]) => (
            <div key={category} style={{ marginBottom: 22 }}>
              <h3
                style={{
                  color: "var(--accent)",
                  marginBottom: 10,
                  fontSize: "0.95rem",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                📦 {category}
              </h3>
              {Object.entries(models).map(([model, colors]) => {
                const tot = Object.values(colors).reduce(
                  (a, c) => ({
                    qty: a.qty + c.qty,
                    revenue: a.revenue + c.revenue,
                    cost: a.cost + c.cost,
                    profit: a.profit + c.profit,
                  }),
                  { qty: 0, revenue: 0, cost: 0, profit: 0 },
                );
                return (
                  <div
                    key={model}
                    className="card"
                    style={{ marginBottom: 10 }}
                  >
                    {/* Model header */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 10,
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: "0.92rem" }}>
                        {model}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          fontSize: "0.78rem",
                          flexWrap: "wrap",
                        }}
                      >
                        <span>
                          Sold: <strong>{tot.qty}</strong>
                        </span>
                        <span>
                          Rev:{" "}
                          <strong className="text-accent">
                            ৳{tot.revenue.toLocaleString()}
                          </strong>
                        </span>
                        <span>
                          Profit:{" "}
                          <strong
                            className={
                              tot.profit >= 0 ? "text-profit" : "text-loss"
                            }
                          >
                            ৳{tot.profit.toLocaleString()}
                          </strong>
                        </span>
                      </div>
                    </div>

                    {/* Desktop breakdown table */}
                    <div className="table-wrap hide-mobile">
                      <table>
                        <thead>
                          <tr>
                            <th>Color</th>
                            <th>Sold</th>
                            <th>Stock Left</th>
                            <th>Buy Cost</th>
                            <th>Revenue</th>
                            <th>Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(colors).map(([color, data]) => {
                            const sl = getColorStock(category, model, color);
                            return (
                              <tr key={color}>
                                <td>
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        background: colorHex(color),
                                        display: "inline-block",
                                      }}
                                    ></span>
                                    {color}
                                  </span>
                                </td>
                                <td style={{ fontWeight: 600 }}>{data.qty}</td>
                                <td
                                  style={{
                                    fontWeight: 600,
                                    color:
                                      sl === 0
                                        ? "var(--danger)"
                                        : "var(--success)",
                                  }}
                                >
                                  {sl}
                                </td>
                                <td className="text-loss">
                                  ৳{data.cost.toLocaleString()}
                                </td>
                                <td className="text-accent">
                                  ৳{data.revenue.toLocaleString()}
                                </td>
                                <td
                                  className={
                                    data.profit >= 0
                                      ? "text-profit"
                                      : "text-loss"
                                  }
                                  style={{ fontWeight: 700 }}
                                >
                                  ৳{data.profit.toLocaleString()}
                                </td>
                              </tr>
                            );
                          })}
                          <tr
                            style={{
                              background: "var(--bg-hover)",
                              fontWeight: 700,
                            }}
                          >
                            <td>Total</td>
                            <td>{tot.qty}</td>
                            <td>—</td>
                            <td className="text-loss">
                              ৳{tot.cost.toLocaleString()}
                            </td>
                            <td className="text-accent">
                              ৳{tot.revenue.toLocaleString()}
                            </td>
                            <td
                              className={
                                tot.profit >= 0 ? "text-profit" : "text-loss"
                              }
                            >
                              ৳{tot.profit.toLocaleString()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile breakdown cards */}
                    <div className="show-mobile">
                      {Object.entries(colors).map(([color, data]) => {
                        const sl = getColorStock(category, model, color);
                        return (
                          <div
                            key={color}
                            style={{
                              background: "var(--bg-primary)",
                              borderRadius: 8,
                              padding: "10px 12px",
                              marginBottom: 6,
                              border: "1px solid var(--border)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 6,
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  fontWeight: 600,
                                }}
                              >
                                <span
                                  style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    background: colorHex(color),
                                    display: "inline-block",
                                    flexShrink: 0,
                                  }}
                                ></span>
                                {color}
                              </span>
                              <span
                                style={{
                                  fontSize: "0.78rem",
                                  color:
                                    sl === 0
                                      ? "var(--danger)"
                                      : "var(--success)",
                                  fontWeight: 600,
                                }}
                              >
                                Stock: {sl}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 4,
                                fontSize: "0.78rem",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    color: "var(--text-muted)",
                                    fontSize: "0.65rem",
                                  }}
                                >
                                  SOLD
                                </div>
                                <strong>{data.qty}</strong>
                              </div>
                              <div>
                                <div
                                  style={{
                                    color: "var(--text-muted)",
                                    fontSize: "0.65rem",
                                  }}
                                >
                                  REVENUE
                                </div>
                                <strong className="text-accent">
                                  ৳{data.revenue.toLocaleString()}
                                </strong>
                              </div>
                              <div>
                                <div
                                  style={{
                                    color: "var(--text-muted)",
                                    fontSize: "0.65rem",
                                  }}
                                >
                                  PROFIT
                                </div>
                                <strong
                                  className={
                                    data.profit >= 0
                                      ? "text-profit"
                                      : "text-loss"
                                  }
                                >
                                  ৳{data.profit.toLocaleString()}
                                </strong>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {/* Mobile total row */}
                      <div
                        style={{
                          background: "var(--bg-hover)",
                          borderRadius: 8,
                          padding: "10px 12px",
                          border: "1px solid var(--border)",
                          fontWeight: 700,
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr",
                            gap: 4,
                            fontSize: "0.8rem",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                color: "var(--text-muted)",
                                fontSize: "0.65rem",
                              }}
                            >
                              TOTAL SOLD
                            </div>
                            <strong>{tot.qty}</strong>
                          </div>
                          <div>
                            <div
                              style={{
                                color: "var(--text-muted)",
                                fontSize: "0.65rem",
                              }}
                            >
                              REVENUE
                            </div>
                            <strong className="text-accent">
                              ৳{tot.revenue.toLocaleString()}
                            </strong>
                          </div>
                          <div>
                            <div
                              style={{
                                color: "var(--text-muted)",
                                fontSize: "0.65rem",
                              }}
                            >
                              PROFIT
                            </div>
                            <strong
                              className={
                                tot.profit >= 0 ? "text-profit" : "text-loss"
                              }
                            >
                              ৳{tot.profit.toLocaleString()}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* ── MODAL ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editSale ? "Edit Sale" : "Record Sale"}</h3>

            {!editSale && (
              <>
                <div className="form-group">
                  <label>Product</label>
                  <select
                    value={form.productId}
                    onChange={(e) => handleProductChange(e.target.value)}
                  >
                    <option value="">Select product...</option>
                    {products.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name} — {p.model}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedProduct && (
                  <div className="form-group">
                    <label>Color</label>
                    <select
                      value={form.color}
                      onChange={(e) => handleColorChange(e.target.value)}
                    >
                      <option value="">Select color...</option>
                      {selectedProduct.colors.map((c) => (
                        <option
                          key={c.color}
                          value={c.color}
                          disabled={c.stock === 0}
                        >
                          {c.color} (Stock: {c.stock})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {editSale && (
              <div
                style={{
                  background: "var(--bg-primary)",
                  padding: 10,
                  borderRadius: 8,
                  marginBottom: 12,
                  fontSize: "0.84rem",
                  color: "var(--text-secondary)",
                }}
              >
                <strong style={{ color: "var(--text-primary)" }}>
                  {editSale.productName}
                </strong>{" "}
                — {editSale.model} / {editSale.color}
              </div>
            )}

            <div className="grid-2">
              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  value={form.quantity}
                  onChange={(e) =>
                    setForm({ ...form, quantity: +e.target.value })
                  }
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Sell Price ৳</label>
                <input
                  type="number"
                  value={form.sellPrice}
                  onChange={(e) =>
                    setForm({ ...form, sellPrice: +e.target.value })
                  }
                  min="0"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Note (optional)</label>
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Customer name, reference..."
              />
            </div>

            {form.quantity > 0 && form.sellPrice > 0 && (
              <div
                style={{
                  background: "var(--bg-primary)",
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 14,
                  border: "1px solid var(--border-accent)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.83rem",
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Revenue:
                  </span>
                  <span className="text-accent">
                    ৳{(form.sellPrice * form.quantity).toLocaleString()}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.83rem",
                    marginTop: 4,
                  }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    Est. Profit:
                  </span>
                  <span className="text-profit">
                    ৳
                    {(
                      (form.sellPrice -
                        (editSale
                          ? editSale.buyPrice
                          : selectedProduct?.colors.find(
                              (c) => c.color === form.color,
                            )?.buyPrice || 0)) *
                      form.quantity
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submit}>
                {editSale
                  ? "Update Sale"
                  : isAdmin
                    ? "Record Sale"
                    : "Submit for Approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function colorHex(name) {
  const map = {
    black: "#222",
    white: "#eee",
    blue: "#4a90d9",
    red: "#e74c3c",
    green: "#2ecc71",
    purple: "#9b59b6",
    silver: "#aaa",
    gold: "#f1c40f",
    navy: "#2c3e50",
    orange: "#e67e22",
    yellow: "#f9ca24",
    "rose gold": "#b5838d",
  };
  return map[name?.toLowerCase()] || "#555";
}
